import { Logger } from './Logger'

/**
 * Performance utilities for tracking and measuring operations.
 * Provides reusable functions for marking, measuring, and logging performance metrics.
 * All performance logs are automatically prefixed with "[PERF]".
 */

/**
 * Marks the start of a performance measurement.
 * Auto-generates mark name as "{name}-start".
 * @param name - The base name for the performance measurement (e.g., 'login-loader')
 */
export const markPerformanceStart = (name: string): void => {
  performance.mark(`${name}-start`)
}

/**
 * Marks the end of a performance measurement and logs the duration.
 * Auto-generates mark names as "{name}-start" and "{name}-end", and measure name as "{name}-duration".
 * @param name - The base name for the performance measurement (e.g., 'login-loader')
 * @param logger - Logger instance for outputting the performance metric
 * @returns The duration in seconds, or null if the start mark doesn't exist
 */
export const markPerformanceEnd = (name: string, logger: Logger): number | null => {
  const startMarkName = `${name}-start`
  const endMarkName = `${name}-end`
  const measureName = `${name}-duration`

  // Check if start mark exists before measuring
  const marks = performance.getEntriesByName(startMarkName)
  if (marks.length > 0) {
    performance.mark(endMarkName)
    performance.measure(measureName, startMarkName, endMarkName)
    const measures = performance.getEntriesByName(measureName)
    if (measures.length > 0) {
      const durationMs = measures[0].duration
      const durationS = (durationMs / 1000).toFixed(2)
      logger.debug(`[PERF] ${name}: ${durationS}s`)
      return parseFloat(durationS)
    }
  }
  return null
}

/**
 * Measures the duration between two marks and returns the value without logging.
 * Auto-generates mark names as "{name}-start" and "{name}-end", and measure name as "{name}-duration".
 * @param name - The base name for the performance measurement
 * @returns The duration in seconds, or null if the start mark doesn't exist
 */
export const measurePerformance = (name: string): number | null => {
  const startMarkName = `${name}-start`
  const endMarkName = `${name}-end`
  const measureName = `${name}-duration`

  const marks = performance.getEntriesByName(startMarkName)
  if (marks.length > 0) {
    performance.mark(endMarkName)
    performance.measure(measureName, startMarkName, endMarkName)
    const measures = performance.getEntriesByName(measureName)
    if (measures.length > 0) {
      const durationMs = measures[0].duration
      const durationS = parseFloat((durationMs / 1000).toFixed(3))
      return durationS
    }
  }
  return null
}

/**
 * Clears performance marks and measures by base name.
 * Clears "{name}-start", "{name}-end", and "{name}-duration".
 * @param names - Array of base names to clear
 */
export const clearPerformanceMarks = (...names: string[]): void => {
  names.forEach((name) => {
    performance.clearMarks(`${name}-start`)
    performance.clearMarks(`${name}-end`)
    performance.clearMeasures(`${name}-duration`)
  })
}
